import React, { useEffect, useRef, useState } from "react";
import { Provider, useDispatch } from "react-redux";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { ThemeProvider } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-light-svg-icons";
import { useV2NotificationsUnreadCountQuery } from "@generated";
import useFlags from "@common/hooks/useFlags";
import useOauth from "@utils/customHooks/useOauth";
import NotificationDropdown from "@components/NotificationDropdown";
import store from "../../store";
import theme from "../../themes/theme";
import { setAccountSlug } from "../../slices/slug";
import { setCredentials } from "../../slices/auth";

interface NotificationBellV1Props {
  account_slug: string;
}

// Standalone mount for the classic Rails navbar (_navbar.html.haml) — there's
// no existing React root there to nest into, so this bootstraps its own
// Provider/ThemeProvider/LDProvider and its own auth token.
const NotificationBellV1: React.FC<NotificationBellV1Props> = ({
  account_slug,
}) => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <LDProvider
          clientSideID={
            process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
          }
        >
          <NotificationBellV1Content accountSlug={account_slug} />
        </LDProvider>
      </ThemeProvider>
    </Provider>
  );
};

const NotificationBellV1Content: React.FC<{ accountSlug: string }> = ({
  accountSlug,
}) => {
  const [accessToken] = useOauth();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(setAccountSlug(accountSlug));
    if (accessToken) {
      dispatch(setCredentials({ token: accessToken, authorized: true }));
    }
  }, [accessToken, accountSlug, dispatch]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const flags = useFlags();
  const enabled = Boolean(flags.releaseNotificationManagement);
  const { data, isLoading, refetch } = useV2NotificationsUnreadCountQuery(
    undefined,
    {
      skip: !enabled || !accessToken,
    }
  );

  if (!enabled) return null;

  const unreadCount = data?.["unread-count"] ?? 0;
  const showBadge = !isLoading && unreadCount > 0;
  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const ariaLabel = showBadge
    ? `Notifications, ${unreadCount} unread`
    : "Notifications";

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        id="nav-notifications"
        aria-label={ariaLabel}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          margin: "10px",
          cursor: "pointer",
        }}
      >
        <span style={{ position: "relative", display: "inline-flex" }}>
          <FontAwesomeIcon
            icon={faBell}
            style={{ fontSize: 20, color: "#fff" }}
          />
          {showBadge && (
            <span
              data-testid="navbar-notification-badge"
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -6,
                right: -8,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                boxSizing: "border-box",
                borderRadius: 8,
                backgroundColor: "#DC3545",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                lineHeight: "16px",
                textAlign: "center",
              }}
            >
              {badgeLabel}
            </span>
          )}
        </span>
      </button>
      {open && (
        <NotificationDropdown align="right" refetchUnreadCount={refetch} />
      )}
    </div>
  );
};

export default NotificationBellV1;
